<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Early Leave Office
      </oxd-text>

      <oxd-divider />

      <oxd-form
        :key="formInstanceKey"
        :loading="isLoading"
        @submit-valid="onSave"
      >
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="form.leaveDate"
                :label="$t('general.date')"
                :rules="rules.leaveDate"
                :years="yearsArray"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <time-input
                v-model="form.expectedTime"
                :label="$t('leave.expected_departure_time')"
                :rules="rules.expectedTime"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.timezoneDisplay"
                :label="$t('attendance.timezone')"
                disabled
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.comment"
                type="textarea"
                :label="$t('general.comments')"
                :rules="rules.comment"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <submit-button :disabled="isLoading" :label="$t('general.submit')" />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import {yearRange} from '@ohrm/core/util/helper/year-range';
import {
  required,
  validDateFormat,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {
  buildSubmitPayload,
  createTimeRangeValidator,
  formatShiftTime12h,
  formatTimezoneLabel,
  getBrowserTimezone,
} from '@/orangehrmLeavePlugin/util/shiftPermissionExpectedTime';

export default {
  name: 'EarlyLeaveApply',

  props: {
    shiftStartTime: {
      type: String,
      default: '',
    },
    shiftEndTime: {
      type: String,
      default: '',
    },
    workTimezone: {
      type: String,
      default: '',
    },
    defaultLeaveDate: {
      type: String,
      default: '',
    },
    minEarlyDepartureTime: {
      type: String,
      default: '',
    },
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/early-leave-requests',
    );
    const {userDateFormat} = useDateFormat();

    return {
      http,
      userDateFormat,
    };
  },

  data() {
    return {
      isLoading: false,
      formInstanceKey: 0,
      yearsArray: [...yearRange()],
      form: {
        leaveDate: this.defaultLeaveDate,
        expectedTime: '',
        timezoneDisplay: formatTimezoneLabel(getBrowserTimezone()),
        comment: '',
      },
    };
  },

  computed: {
    departureErrorMessage() {
      return this.$t('leave.expected_departure_outside_window', {
        minTime: formatShiftTime12h(this.minEarlyDepartureTime),
        maxTime: formatShiftTime12h(this.shiftEndTime),
      });
    },
    rules() {
      return {
        leaveDate: [required, validDateFormat(this.userDateFormat)],
        expectedTime: [
          required,
          createTimeRangeValidator('early_leave', {
            leaveDate: () => this.form.leaveDate,
            shiftStartTime: this.shiftStartTime,
            shiftEndTime: this.shiftEndTime,
            workTimezone: this.workTimezone,
            minEarlyDepartureTime: this.minEarlyDepartureTime,
            departureErrorMessage: () => this.departureErrorMessage,
          }),
        ],
        comment: [required, shouldNotExceedCharLength(255)],
      };
    },
  },

  methods: {
    createEmptyForm() {
      return {
        leaveDate: this.defaultLeaveDate,
        expectedTime: '',
        timezoneDisplay: formatTimezoneLabel(getBrowserTimezone()),
        comment: '',
      };
    },
    async onSave() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      try {
        await this.http.create(
          buildSubmitPayload({
            leaveDate: this.form.leaveDate,
            expectedTime: this.form.expectedTime,
            comment: this.form.comment,
          }),
        );
        this.$toast.saveSuccess();
        this.form = this.createEmptyForm();
        this.formInstanceKey += 1;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
